/* eslint-disable lodash/prefer-lodash-method */
import './App.css';

import { initAll, track } from '@amplitude/unified';
import { AppState, Auth0Provider } from '@auth0/auth0-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createQueryClient } from 'api/createQueryClient';
import TrialModal from 'components/TrialModal/TrialModal.component';
import { Tier } from 'consts/auth';
import { CHAT_LIMIT_MODAL_ID, TRIAL_EXPIRED_MODAL_ID, UPGRADE_ACCESS_MODAL_ID } from 'consts/modals';
import { ModalsProvider, useModals } from 'contexts/modals';
import { useHasTier, useHealEmptyFirstTokenPermissions, useTrial } from 'hooks/use-auth';
import { PageLayout } from 'layout';
import { ErrorBoundary } from 'layout';
import { submitUpgradeRequest } from 'lib/request-upgrade';
import React, { useEffect, useRef, useState } from 'react';
import { Route, Router, Switch } from 'react-router-dom';
import { Routes } from 'routes';

import { history } from './lib/history';

// Lazy-loaded so it doesn't bloat the main bundle for normal users. Mounted
// at the top level in App.js (outside PageLayoutContainer) so the headless
// renderer gets bare chrome — no sidebar, Sign In button, or cookie banner.
const PrintArtifactPage = React.lazy(() => import('pages/PrintArtifactPage'));

//     : Determine where to put Amplitude LogOnMount

// Session replay is configured entirely in Amplitude (remote config):
// sampling is paced by Dynamic Sampling in Session Replay settings, which
// stays disabled if any SDK passes a local sampleRate — so no replay
// options here. (The unified SDK's key for them would be `sr`; a previous
// `sessionReplay` block was silently ignored.)
initAll(import.meta.env.VITE_AMPLITUDE_KEY, {
  analytics: {
    defaultTracking: {
      attribution: false,
      fileDownloads: false,
      formInteractions: false,
      sessions: true,
      pageViews: {
        trackOn: () => { return !window.location.pathname.includes('/callback'); },
        trackHistoryChanges: 'all',
        eventType: 'page_view',
      },
    },
  },
});

const onRedirectCallback = (appState?: AppState) => {
  // If using a Hash Router, you need to use window.history.replaceState to
  // remove the `code` and `state` query parameters from the callback url.
  // window.history.replaceState({}, document.title, window.location.pathname);
  history.replace((appState && appState.returnTo) || window.location.pathname);
};

// Create a client (wires Sentry capture into every query/mutation failure)
const queryClient = createQueryClient();

interface RootProviderProps {
  children: React.ReactNode;
}

const RootProvider = ({ children }: RootProviderProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ModalsProvider>
        <Router history={history}>
          <Auth0Provider
            domain={import.meta.env.VITE_AUTH_DOMAIN}
            clientId={import.meta.env.VITE_AUTH_CLIENT_ID}
            authorizationParams={{
              audience: import.meta.env.VITE_AUTH_API_URL,
              scope: 'openid profile email',
              redirect_uri: `${window.location.origin}/callback`,
              // Default connection: passwordless email OTP. Universal Login
              // cannot offer a password-vs-code choice across two
              // connections, so the /sign-in page (lib/login-method) picks
              // one per call; this provider-level default covers every other
              // loginWithRedirect (e.g. the signup button). Existing password
              // users are safe on the OTP path: their first OTP login mints an
              // email| identity that the link-user-accounts Auth0 action
              // merges into their database identity, preserving paid claims.
              connection: 'email',
            }}
            onRedirectCallback={onRedirectCallback}
            cacheLocation='localstorage'
            useRefreshTokens={true}
          >
            {children}
          </Auth0Provider>
        </Router>
      </ModalsProvider>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition='bottom-left' />
    </QueryClientProvider>
  );
};

const Page = () => {
  const [fromAuth0Login, setFromAuth0Login] = useState(false);
  const mountTracked = useRef(false);

  const { openModal, closeModal } = useModals();
  const trialAlertKey = 'trial-alert-time';

  const isFreeTier = useHasTier(Tier.Free);
  const trial = useTrial();
  useHealEmptyFirstTokenPermissions();

  useEffect(() => {
    const url = `${history.location.pathname}${history.location.search}`;
    const fromCallback = history.location.pathname.includes('/callback');
    if (fromCallback) setFromAuth0Login(true);

    // Fire once per SPA boot. The effect re-runs when fromAuth0Login /
    // isFreeTier flip as auth resolves, which used to re-track and inflate
    // app_mounted counts (each login boot also double-fired: login_callback,
    // then app_mounted after the redirect flipped fromAuth0Login).
    if (!mountTracked.current) {
      mountTracked.current = true;
      const loginCallbackEvent = { name: 'login_callback', props: {} };
      const appMountedEvent = { name: 'app_mounted', props: { url } };
      const event = fromCallback ? loginCallbackEvent : appMountedEvent;
      track(event.name, event.props);
    }

    // Was the start-a-trial pitch; now the same generic upgrade prompt every
    // other locked surface opens. The localStorage key keeps its old name so
    // existing users' 10h cadence isn't reset by the rename.
    const showUpgradePrompt = () => {
      const time = new Date().getTime();
      localStorage.setItem(trialAlertKey, `${time}`);
      openModal(UPGRADE_ACCESS_MODAL_ID);
    };

    // Check every 10h
    const checkAlertTime = (lastUpdated = 0, threshold = 10 * 60 * 60 * 1000) => {
      const now = new Date().getTime();
      const diff = now - lastUpdated;
      if (diff > threshold) showUpgradePrompt();
    };

    if (isFreeTier) {
      if (fromAuth0Login) showUpgradePrompt();
      const lastPromptTime = localStorage.getItem(trialAlertKey);
      if (lastPromptTime) checkAlertTime(+lastPromptTime);
    }
  }, [openModal, fromAuth0Login, isFreeTier]);

  useEffect(() => {
    if (trial && trial.isExpired) {
      openModal(TRIAL_EXPIRED_MODAL_ID);
    }
  }, [openModal, trial]);

  return (
    <React.Fragment>
      {/* document.title is owned by DocumentTitleProvider inside PageLayout;
          /print/artifact keeps the static <title> from index.html. */}
      {/*
          /print/artifact is intercepted here, BEFORE PageLayoutContainer, so
          the headless renderer gets a bare page (no sidebar, Sign In button,
          or cookie banner). Auth comes from the ?token= query param, not from
          PageLayout's Auth0 session, so PageLayout would only get in the way.
        */}
      <Switch>
        <Route exact path='/print/artifact'>
          {/*
              Tailwind's preflight normalizes the browser body margin, so the
              screenshot has no extra white border. ErrorBoundary keeps
              render-time chart failures from taking down the whole tree —
              without it the renderer would never see __artifactReady and the
              screenshot would time out.
            */}
          <ErrorBoundary>
            <React.Suspense fallback={null}>
              <PrintArtifactPage />
            </React.Suspense>
          </ErrorBoundary>
        </Route>
        <Route>
          {/*
              An uncaught render error here (e.g. OILYTICS-DEVELOPMENT-REACT-GZ,
              a Radix Presence ref-loop on /watchlists/89) used to propagate
              past Auth0Provider with nothing to catch it, tearing down the
              whole tree right after a successful login — indistinguishable
              from being logged out. Catch it here instead.
            */}
          <ErrorBoundary>
            <PageLayout>
              <Routes/>
            </PageLayout>
          </ErrorBoundary>
        </Route>
      </Switch>
      <TrialModal
        id={TRIAL_EXPIRED_MODAL_ID}
        title='Trial expired'
        content={`Contact us to become a full subscriber with access to the HiLo AI agent, along with
        our daily market reports, pricing dashboards, and core fundamentals.`}
        acceptText='Contact us'
        cancelText='Not now'
        onClose={(accepted) => {
          closeModal(TRIAL_EXPIRED_MODAL_ID);
          if (accepted) {
            submitUpgradeRequest();
          }
        }}
      />
      <TrialModal
        id={CHAT_LIMIT_MODAL_ID}
        title='Free Usage Limit Reached'
        content={`Contact us to unlock full access to the HiLo AI agent, along with
        our daily market reports, pricing dashboards, and core fundamentals.`}
        acceptText='Contact us'
        cancelText='Not now'
        onClose={(accepted) => {
          closeModal(CHAT_LIMIT_MODAL_ID);
          if (accepted) {
            submitUpgradeRequest();
          }
        }}
      />
      <TrialModal
        id={UPGRADE_ACCESS_MODAL_ID}
        title='Upgrade'
        content={`Contact us to unlock full access to the HiLo AI agent, along with
        our daily market reports, pricing dashboards, and core fundamentals.`}
        acceptText='Contact us'
        cancelText='Not now'
        onClose={(accepted) => {
          closeModal(UPGRADE_ACCESS_MODAL_ID);
          if (accepted) {
            submitUpgradeRequest();
          }
        }}
      />
    </React.Fragment>
  );
};

export const App = () => {
  return (
    <RootProvider>
      <Page />
    </RootProvider>
  );
};
